import dayjs from 'dayjs';

import { useUserFilters } from './useUserFilters';
import { FiltersHelper } from '../helpers/filters.helper';
import { ISchoolCode } from '../types/school.type';
import { IDateToType } from '../types/user.type';

type IFiltersType =
  | 'inscription'
  | 'staff'
  | 'staffEvolution'
  | 'staffStatus'
  | 'staffStatusEvolution'
  | 'staffStatistics'
  | 'staffTravel'
  | 'preInscription'
  | 'preInscriptionSummary'
  | 'preInscriptionAcademy'
  | 'preInscriptionSchool'
  | 'preInscriptionReason'
  | 'sales'
  | 'salesIncome';

export const useFetchDefaultFiltersData = <T extends any>(type: IFiltersType): T => {
  const filters = useUserFilters();

  const school = filters?.school ?? '';
  const schoolYear = filters?.schoolYear ?? '';
  const dateFrom = filters?.dateFrom ? dayjs(filters.dateFrom) : dayjs().subtract(1, 'month');
  const dateTo = FiltersHelper.getDateTo(filters);

  const preInscriptionDateFrom = filters?.preInscriptionDateFrom
    ? dayjs(filters.preInscriptionDateFrom)
    : dayjs().subtract(1, 'month');

  const preInscriptionDateTo =
    filters?.preInscriptionDateToType === IDateToType.today ? dayjs() : dayjs().add(1, 'day');

  const dateComparisonFrom = filters?.dateFromComparison
    ? dayjs(filters.dateFromComparison)
    : dayjs().subtract(1, 'year').subtract(1, 'month');

  const period1SchoolYear = +(filters?.schoolYear ?? dayjs().format('YYYY'));
  const subtractYear = period1SchoolYear - +(filters?.schoolYearComparison ?? period1SchoolYear);
  const dateComparisonTo = FiltersHelper.getDateTo(filters).subtract(subtractYear, 'year');

  switch (type) {
    case 'inscription':
      return {
        school,
        schoolYear,
        schoolLevel: [],
        discipline: [],
        taxon: [],
      } as T;

    case 'staff':
      return {
        dateFrom,
        dateTo,
        productType: '',
        registrationType: '',
        school,
        schoolLevel: [],
        schoolYear,
        partnerships: [],
        withoutPartnerships: false,
        studentStatus: '',
        previousSchool: '',
      } as T;

    case 'staffEvolution':
      return {
        productType: '',
        registrationType: '',
        school,
        schoolLevel: [],
        period1SchoolYear: filters?.schoolYear ?? '',
        period1DateFrom: dateFrom,
        period1DateTo: dateTo,
        period2SchoolYear: filters?.schoolYearComparison ?? '',
        period2DateFrom: dateComparisonFrom,
        period2DateTo: dateComparisonTo,
      } as T;

    case 'staffStatus':
      return {
        school,
        dateFrom,
        dateTo,
        schoolYear,
        productType: '',
      } as T;

    case 'staffStatusEvolution':
      return {
        productType: '',
        school,
        period1SchoolYear: filters?.schoolYear ?? '',
        period1DateFrom: dateFrom,
        period1DateTo: dateTo,
        period2SchoolYear: filters?.schoolYearComparison ?? '',
        period2DateFrom: dateComparisonFrom,
        period2DateTo: dateComparisonTo,
      } as T;

    case 'staffStatistics':
      return {
        dateFrom,
        dateTo,
        productType: '',
        registrationType: '',
        school,
        schoolLevel: [],
        schoolYear,
        partnerships: [],
        withoutPartnerships: false,
        studentStatus: '',
      } as T;

    case 'staffTravel':
      return {
        dateFrom,
        dateTo,
        school,
        schoolYear,
      } as T;

    case 'preInscription':
      return {
        school,
        schoolYear: filters?.preInscriptionSchoolYear ?? '',
        registrationType: '',
        dateFrom: preInscriptionDateFrom,
        dateTo: preInscriptionDateTo,
        schoolLevel: [],
        academy: [],
        department: [],
        reason: [],
        situation: [],
        responseDa: '',
        inscription: '',
        envoiRapo: '',
        responseRapo: '',
        recoursTa: '',
        responseTa: '',
        processedBy: '',
      } as T;

    case 'preInscriptionSummary':
      return {
        school,
        schoolYear: filters?.preInscriptionSchoolYear ?? '',
        groupCode: [],
        reason: [],
      } as T;

    case 'preInscriptionAcademy':
      return {
        school,
        schoolYear: filters?.preInscriptionSchoolYear ?? '',
        academy: [],
      } as T;

    case 'preInscriptionSchool':
      return {
        schoolYear: filters?.preInscriptionSchoolYear ?? '',
      } as T;

    case 'preInscriptionReason':
      return {
        school,
        schoolYear: filters?.preInscriptionSchoolYear ?? '',
      } as T;

    case 'sales':
      return {
        dateFrom,
        dateTo,
        school,
        schoolYear,
        year: '',
        month: '',
        week: '',
      } as T;

    case 'salesIncome':
      return {
        dateFrom,
        dateTo,
        school: filters?.school ?? ISchoolCode.eib,
        schoolYear,
        schoolLevel: [],
        year: '',
        month: '',
      } as T;

    default:
      return {} as T;
  }
};
